// Version 2 - Handles both pure Creole and mixed French/Creole text
export const PHONOLOGICAL_RULES_CONFIG = {
  FRENCH_REPLACEMENTS: {
    name: 'French Replacements',
    description: 'Convert French words to Creole pronunciation',
    enabled: true,
    rules: {
      french_haitien_ayisyen: {
        name: 'Haïtien → Ayisyen',
        description: 'Haïtien/Haitien → Ayisyen',
        enabled: true
      },
      french_homme_nonm: {
        name: 'Homme → Nonm',
        description: 'homme(s) → nonm',
        enabled: true
      },
      french_femme_fanm: {
        name: 'Femme → Fanm',
        description: 'femme(s) → fanm',
        enabled: true
      },
      french_enfant_timoun: {
        name: 'Enfant → Timoun',
        description: 'enfant(s) → timoun',
        enabled: true
      },
      french_maison_kay: {
        name: 'Maison → Kay',
        description: 'maison(s) → kay',
        enabled: true
      },
      french_ecole_lekol: {
        name: 'École → Lekòl',
        description: 'école → lekòl',
        enabled: true
      },
      french_eglise_legliz: {
        name: 'Église → Legliz',
        description: 'église → legliz',
        enabled: true
      },
      french_eau_dlo: {
        name: 'Eau → Dlo',
        description: 'eau(x) → dlo',
        enabled: true
      },
      french_travail_travay: {
        name: 'Travail → Travay',
        description: 'travail → travay',
        enabled: true
      },
      french_beaucoup_anpil: {
        name: 'Beaucoup → Anpil',
        description: 'beaucoup → anpil',
        enabled: true
      },
      french_bonjour_bonjou: {
        name: 'Bonjour → Bonjou',
        description: 'bonjour → bonjou',
        enabled: true
      },
      french_merci_mesi: {
        name: 'Merci → Mèsi',
        description: 'merci → mèsi',
        enabled: true
      },
      french_pardon_padon: {
        name: 'Pardon → Padon',
        description: 'pardon → padon',
        enabled: true
      },
      french_aujourd_jodi: {
        name: "Aujourd'hui → Jodi a",
        description: "aujourd'hui → jodi a",
        enabled: true
      },
      french_maintenant_kounye: {
        name: 'Maintenant → Kounye a',
        description: 'maintenant → kounye a',
        enabled: true
      }
    }
  },
  NASALIZATION: {
    name: 'Nasalization',
    description: 'Handle nasal vowels (an→ã, en→ẽ, on→õ)',
    enabled: false,
    rules: {
      an_nasalization: {
        name: 'AN → Ã (final)',
        description: 'Word-final "an" → ã (jan → jã)',
        enabled: true
      },
      en_nasalization: {
        name: 'EN → Ẽ (final)',
        description: 'Word-final "en" → ẽ (gen → gẽ)',
        enabled: true
      },
      on_nasalization: {
        name: 'ON → Õ (final)',
        description: 'Word-final "on" → õ (bon → bõ)',
        enabled: true
      },
      an_internal_nasalization: {
        name: 'AN → Ã (internal)',
        description: '"an" before consonants → ã (anpil → ãpil)',
        enabled: true
      },
      en_internal_nasalization: {
        name: 'EN → Ẽ (internal)',
        description: '"en" before consonants → ẽ (gentan → gẽtã)',
        enabled: true
      },
      on_internal_nasalization: {
        name: 'ON → Õ (internal)',
        description: '"on" before consonants → õ (konsa → kõsa)',
        enabled: true
      }
    }
  },
  CONTRACTIONS: {
    name: 'Contractions',
    description: 'Spoken contractions (mwen→m, ou→w)',
    enabled: false,
    rules: {
      mwen_contraction: {
        name: 'MWEN → M',
        description: 'mwen + consonant → m (mwen konnen → m konnen)',
        enabled: true
      },
      ou_contraction_after_ki: {
        name: 'OU → W (after ki)',
        description: 'ou after ki → w (ki ou ye → ki w ye)',
        enabled: true
      },
      ou_contraction_after_kote: {
        name: 'OU → W (after kote)',
        description: 'ou after kote → w (kote ou ale → kote w ale)',
        enabled: true
      },
      pa_p_contraction: {
        name: 'PA → P\' (informal)',
        description: 'pa + vowel → p\' (pa ale → p\'ale)',
        enabled: false  // Disabled by default - very informal
      }
    }
  },
  R_MODIFICATION: {
    name: 'R Modification',
    description: 'Dialect-specific r→w changes',
    enabled: false,  // Disabled by default - dialect specific
    rules: {
      r_to_w_initial: {
        name: 'Initial R → W',
        description: 'Initial r + vowel → w (rete → wete)',
        enabled: false
      }
    }
  }
};

// Helper function to get enabled categories and rules
// Infer's logic: If a category is enabled, all its rules are implicitly enabled
// unless specific rules are explicitly listed in enabled_rules (then only those are enabled)
export function getEnabledRules(rulesState) {
  const enabled_categories = [];
  const enabled_rules = [];
  
  Object.entries(rulesState).forEach(([categoryKey, category]) => {
    if (category.enabled) {
      enabled_categories.push(categoryKey);
      
      // Check if all rules in this category are enabled
      if (category.rules) {
        const allRulesEnabled = Object.values(category.rules).every(rule => rule.enabled);
        
        // If NOT all rules are enabled, we need to specify which ones are
        if (!allRulesEnabled) {
          Object.entries(category.rules).forEach(([ruleKey, rule]) => {
            if (rule.enabled) {
              enabled_rules.push(ruleKey);
            }
          });
        }
        // If all rules are enabled, we don't add them to enabled_rules
        // because the category being enabled implies all rules are enabled
      }
    }
  });
  
  return { enabled_categories, enabled_rules };
}

// Helper function to initialize state from localStorage or defaults
export function initializeRulesState() {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('phonologicalRulesV2');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Check if this is the old v1 structure (has FINAL_E_DELETION, etc.)
        if (parsed.FINAL_E_DELETION || parsed.H_ASPIRATION || parsed.CONSONANT_CLUSTERS) {
          console.log('Migrating from v1 to v2 phonological rules');
          // Clear old rules and use new defaults
          localStorage.removeItem('phonologicalRulesV2');
          return PHONOLOGICAL_RULES_CONFIG;
        }
        // Check if this is missing the new FRENCH_REPLACEMENTS category
        if (!parsed.FRENCH_REPLACEMENTS) {
          console.log('Updating to v2 with French replacements');
          // Add the new category while preserving user's existing settings
          return {
            FRENCH_REPLACEMENTS: PHONOLOGICAL_RULES_CONFIG.FRENCH_REPLACEMENTS,
            ...parsed
          };
        }
        return parsed;
      } catch (e) {
        console.error('Failed to parse saved rules:', e);
      }
    }
  }
  return PHONOLOGICAL_RULES_CONFIG;
}